import { ApiResponse, UploadResult } from '@packages/global';
import { httpClient } from '../httpClient';

/**
 * 通过服务器代传文件到 OSS
 * 用于客户端直传 OSS 失败（如客户端到 OSS 网络不通）时的回退方案
 */

type UploadKind = 'image' | 'audio' | 'video';

const FIELD_NAME: Record<UploadKind, string> = {
  image: 'image',
  audio: 'audio',
  video: 'video',
};

const ENDPOINT: Record<UploadKind, string> = {
  image: '/user/upload/image',
  audio: '/user/upload/audio',
  video: '/user/upload/video',
};

const MAX_SIZE: Record<UploadKind, number> = {
  image: 20 * 1024 * 1024,    // 20MB（与后端一致）
  audio: 50 * 1024 * 1024,    // 50MB
  video: 200 * 1024 * 1024,   // 200MB
};

function detectKind(file: File): UploadKind | null {
  const mime = (file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  return null;
}

async function uploadByKind(
  file: File,
  kind: UploadKind,
  onProgress?: (percent: number) => void
): Promise<UploadResult> {
  if (file.size > MAX_SIZE[kind]) {
    const limitMb = Math.round(MAX_SIZE[kind] / 1024 / 1024);
    throw new Error(`文件大小超过服务器代传限制 ${limitMb}MB，请压缩后再试`);
  }

  const formData = new FormData();
  formData.append(FIELD_NAME[kind], file);

  const response = await httpClient.instance.post<ApiResponse<UploadResult>>(
    ENDPOINT[kind],
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      // 服务器代传通常较慢，特别是视频
      timeout: kind === 'video' ? 10 * 60 * 1000 : 5 * 60 * 1000,
      onUploadProgress: (e) => {
        if (e.total && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      },
    }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || '服务器代传失败');
  }
  return response.data.data;
}

export const fileUploadApi = {
  /** 上传图片到服务器代传通道 */
  uploadImage(file: File, onProgress?: (percent: number) => void): Promise<UploadResult> {
    return uploadByKind(file, 'image', onProgress);
  },

  /** 上传音频到服务器代传通道 */
  uploadAudio(file: File, onProgress?: (percent: number) => void): Promise<UploadResult> {
    return uploadByKind(file, 'audio', onProgress);
  },

  /** 上传视频到服务器代传通道 */
  uploadVideo(file: File, onProgress?: (percent: number) => void): Promise<UploadResult> {
    return uploadByKind(file, 'video', onProgress);
  },

  /**
   * 根据文件 MIME 类型自动选择上传通道
   * 无法识别 MIME 时默认按 image 处理
   */
  uploadAuto(file: File, onProgress?: (percent: number) => void): Promise<UploadResult> {
    const kind = detectKind(file) ?? 'image';
    return uploadByKind(file, kind, onProgress);
  },
};
